import PropTypes from 'prop-types';
import {
  GENERAL_PARTNER_SHARE,
  GRAPH_COPY,
  NODE_KIND,
  NOT_APPLICABLE_SHARE,
  SHARE_IN_TARGET_BASIS,
  SHAREHOLDER_LEGEND_ICONS
} from './graphConstants';
import CardFigures from './CardFigures';
import { useGraphContext } from './GraphContext';

// Card icon by entity type; the same SVGs as the legend.
export const KIND_ICONS = {
  target: SHAREHOLDER_LEGEND_ICONS.target,
  company: SHAREHOLDER_LEGEND_ICONS.company,
  person: SHAREHOLDER_LEGEND_ICONS.person,
  subsidiary: SHAREHOLDER_LEGEND_ICONS.subsidiary,
  entity: SHAREHOLDER_LEGEND_ICONS.entity
};

// Header text when a node has no location.
export const KIND_LABELS = {
  target: 'Target',
  company: 'Organization',
  person: 'Individual',
  subsidiary: 'Subsidiary',
  entity: 'Shareholders'
};

export const BADGE_LABELS = {
  target: 'Target',
  ultimate: 'Ultimate owner',
  generalPartner: 'General partner'
};

export const CardBadges = ({ badges }) =>
  badges.length ? (
    <span className="sg-badges">
      {badges.map((badge) => (
        <span key={badge} className={`sg-badge sg-badge--${badge}`}>
          {BADGE_LABELS[badge] ?? badge}
        </span>
      ))}
    </span>
  ) : null;

CardBadges.propTypes = {
  badges: PropTypes.arrayOf(PropTypes.string).isRequired
};

/** '+1 owner', '+3 owners'; with `suffix` 'above' for the phone list. */
export const formatHiddenOwners = (count, suffix = '') =>
  [`+${count} owner${count === 1 ? '' : 's'}`, suffix].filter(Boolean).join(' ');

export const KindIcon = ({ entityType }) => (
  <img
    className={`sg-kind-icon sg-kind-icon--${entityType}`}
    src={KIND_ICONS[entityType] ?? KIND_ICONS.company}
    alt=""
    aria-hidden="true"
  />
);

KindIcon.propTypes = {
  entityType: PropTypes.string
};

// The kind icon in a tile that spans the name and location lines, like the logo in the company
// name search.
export const KindSlot = ({ entityType }) => (
  <span className="sg-kind-slot" aria-hidden="true">
    <KindIcon entityType={entityType} />
  </span>
);

KindSlot.propTypes = {
  entityType: PropTypes.string
};

/**
 * Share chip classes: the Target pill, grey for a 'GP' share, filled blue for an ultimate owner's
 * beneficial ownership (share_in_target_basis 'beneficial', direct and indirect holdings
 * together), else plain. Both follow the share itself, not the badges: an ultimate owner whose
 * share is its direct stake shows it like any direct shareholder, and a general partner that also
 * holds a stake shows that stake in a plain chip.
 */
export const chipClass = (node) => {
  const { card } = node;
  if (node.kind === NODE_KIND.TARGET) return 'sg-chip sg-chip--target';
  if (card.share === GENERAL_PARTNER_SHARE) return 'sg-chip sg-chip--gp';
  if (card.shareBasis === SHARE_IN_TARGET_BASIS.BENEFICIAL) return 'sg-chip sg-chip--ultimate';
  return 'sg-chip';
};

// No chip for an intermediate owner, whose share in the target is not relevant ('–').
const CompactChip = ({ node }) => {
  const isTarget = node.kind === NODE_KIND.TARGET;
  const text = isTarget ? BADGE_LABELS.target : node.card.share;
  if (!text || text === NOT_APPLICABLE_SHARE) return null;
  return <span className={chipClass(node)}>{text}</span>;
};

CompactChip.propTypes = {
  node: PropTypes.object.isRequired
};

const stopAnd = (handler) => (event) => {
  event.stopPropagation();
  handler?.(event);
};

// '+N owners above' of a collapsed node (phone rows and the levels table).
export const HiddenOwnersButton = ({ node, onToggleCollapsed }) => (
  <button
    type="button"
    className="sg-inline-button nodrag nopan"
    onClick={stopAnd(() => onToggleCollapsed(node.id))}
  >
    {formatHiddenOwners(node.hiddenOwnerCount, 'above')}
  </button>
);

HiddenOwnersButton.propTypes = {
  node: PropTypes.object.isRequired,
  onToggleCollapsed: PropTypes.func.isRequired
};

const joinParts = (parts) => parts.filter(Boolean).join(' · ');

const personLine = (card) => (card.birthYear ? `Born ${card.birthYear}` : null);

const TextLine = ({ text }) =>
  text ? (
    <span className="sg-compact-line" title={text}>
      {text}
    </span>
  ) : null;

TextLine.propTypes = {
  text: PropTypes.string
};

// Phone rows with hidden owners: the line leaves out unknown figures (as in the mockup) and the
// button never shrinks, so '+N owners above' stays visible on a narrow phone. A graph card has its
// own '+N owner' pill, so its figures line keeps the full width.
const FiguresLine = ({ node, variant, status, onToggleCollapsed }) =>
  variant === 'row' && node.hiddenOwnerCount > 0 ? (
    <span className="sg-compact-line sg-compact-line--action">
      <CardFigures figures={node.card.figures} status={status} compact knownOnly />
      <HiddenOwnersButton node={node} onToggleCollapsed={onToggleCollapsed} />
    </span>
  ) : (
    <span className="sg-compact-line">
      <CardFigures figures={node.card.figures} status={status} compact />
    </span>
  );

FiguresLine.propTypes = {
  node: PropTypes.object.isRequired,
  variant: PropTypes.oneOf(['card', 'row']).isRequired,
  status: PropTypes.string,
  onToggleCollapsed: PropTypes.func.isRequired
};

const ShowAllLine = ({ variant, onShowAll }) =>
  variant === 'card' ? (
    <span className="sg-compact-line">
      <button type="button" className="sg-link-button nodrag nopan" onClick={stopAnd(onShowAll)}>
        {GRAPH_COPY.SHOW_ALL}
      </button>
    </span>
  ) : (
    // In a phone row the whole row is the button; this is only its visible label.
    <span className="sg-compact-line sg-link-text">{GRAPH_COPY.SHOW_ALL}</span>
  );

ShowAllLine.propTypes = {
  variant: PropTypes.oneOf(['card', 'row']).isRequired,
  onShowAll: PropTypes.func
};

// The lines under the name: location, holds line, then figures (or born); empty ones are left out.
const CompactLines = ({ node, variant, status, onToggleCollapsed, onShowAll }) => {
  const { card, kind } = node;
  const holds = joinParts(card.holdsLines);
  if (kind === NODE_KIND.TARGET) {
    return (
      <>
        <TextLine text={card.location} />
        <FiguresLine
          node={node}
          variant={variant}
          status={status}
          onToggleCollapsed={onToggleCollapsed}
        />
      </>
    );
  }
  if (kind === NODE_KIND.AGGREGATE) {
    return (
      <>
        <TextLine text={holds} />
        <ShowAllLine variant={variant} onShowAll={onShowAll} />
      </>
    );
  }
  return (
    <>
      <TextLine text={card.location} />
      <TextLine text={holds} />
      {card.entityType === 'person' ? (
        <TextLine text={personLine(card)} />
      ) : (
        <FiguresLine
          node={node}
          variant={variant}
          status={status}
          onToggleCollapsed={onToggleCollapsed}
        />
      )}
    </>
  );
};

CompactLines.propTypes = {
  node: PropTypes.object.isRequired,
  variant: PropTypes.oneOf(['card', 'row']).isRequired,
  status: PropTypes.string,
  onToggleCollapsed: PropTypes.func.isRequired,
  onShowAll: PropTypes.func
};

// Phone rows: one stretched button over the row (the '+N owners above' button sits above it),
// so the row is a single tap target without nesting buttons. The aggregate row is its 'Show all'
// button, and its name says so.
const RowHitArea = ({ node, onActivate, onShowAll }) => {
  const isAggregate = node.kind === NODE_KIND.AGGREGATE;
  const handler = isAggregate ? onShowAll : onActivate;
  return (
    <button
      type="button"
      className="sg-compact-hit"
      aria-label={
        isAggregate ? GRAPH_COPY.SHOW_ALL_LABEL(node.card.ariaLabel) : node.card.ariaLabel
      }
      disabled={!handler}
      onClick={handler ? () => handler(node) : undefined}
    />
  );
};

RowHitArea.propTypes = {
  node: PropTypes.object.isRequired,
  onActivate: PropTypes.func,
  onShowAll: PropTypes.func
};

/**
 * The compact shareholder layout from the mockup ('Compact card: phone and zoomed-out graph'):
 * icon tile, name with the share chip, location, holds line, figures (or born) line.
 *
 * variant 'card' fills the 248x136 graph card when the graph is zoomed out; variant 'row' is a
 * full-width phone list row. Row callbacks receive the graph node.
 */
const CompactShareholderRow = ({
  node,
  variant = 'card',
  figuresStatus,
  onActivate,
  onToggleCollapsed,
  onShowAll,
  className = ''
}) => {
  const context = useGraphContext();
  const status = figuresStatus ?? context.figuresStatus;
  const toggle = onToggleCollapsed ?? context.onToggleNodeCollapsed;
  const showAll = onShowAll ?? context.onShowAllFiltered;
  const hasHitArea = variant === 'row' && node.kind !== NODE_KIND.TARGET;
  const rowHandler = node.kind === NODE_KIND.AGGREGATE ? showAll : onActivate;
  const classes = [
    'sg-compact',
    `sg-compact--${variant}`,
    `sg-compact--${node.kind}`,
    hasHitArea && rowHandler ? 'is-interactive' : '',
    node.card.isUltimate ? 'is-ultimate' : '',
    className
  ].filter(Boolean);

  return (
    <div className={classes.join(' ')} data-node-id={node.id}>
      {hasHitArea && <RowHitArea node={node} onActivate={onActivate} onShowAll={showAll} />}
      <KindSlot entityType={node.card.entityType} />
      <span className="sg-compact-body">
        <span className="sg-compact-title">
          <span className="sg-compact-name" title={node.card.name}>
            {node.card.name}
          </span>
          <CompactChip node={node} />
        </span>
        <CompactLines
          node={node}
          variant={variant}
          status={status}
          onToggleCollapsed={toggle}
          onShowAll={showAll}
        />
      </span>
    </div>
  );
};

CompactShareholderRow.propTypes = {
  node: PropTypes.shape({
    id: PropTypes.string.isRequired,
    kind: PropTypes.oneOf(Object.values(NODE_KIND)).isRequired,
    hiddenOwnerCount: PropTypes.number,
    card: PropTypes.object.isRequired
  }).isRequired,
  variant: PropTypes.oneOf(['card', 'row']),
  figuresStatus: PropTypes.oneOf(['idle', 'loading', 'loaded', 'failed']),
  onActivate: PropTypes.func,
  onToggleCollapsed: PropTypes.func,
  onShowAll: PropTypes.func,
  className: PropTypes.string
};

export default CompactShareholderRow;
